import moment from "moment";
import React, { useCallback, useState } from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import { OTHER_EXCHANGE } from "../../../../services/tickerRequests";
import {
  InstrumentEditor,
  InstrumentEditorDraft,
} from "../../components/InstrumentEditor";
import {
  Card,
  CardHeader,
  CardTitle,
  DefinitionList,
  DraftNotice,
} from "../../styles";
import { useQueueDraft } from "../hooks/useQueueDraft";
import { NoteDate, NoteList } from "../styles";
import { TickerRequestDetailProps, TickerRequestDraft } from "../types";
import { requestKey } from "../utils/toWorkItem";
import { RejectRequestForm } from "./RejectRequestForm";

const toDraft = (
  editor: InstrumentEditorDraft | null,
  reason: string,
): TickerRequestDraft | null =>
  editor || reason.trim() !== "" ? { editor, reason } : null;

export const TickerRequestDetail: React.FC<TickerRequestDetailProps> = ({
  request,
  isSubmitting,
  error,
  onApprove,
  onReject,
  onClearError,
}) => {
  const [draft, updateDraft] = useQueueDraft("request", requestKey(request.id));
  const [isRejecting, setIsRejecting] = useState(Boolean(draft?.reason));
  const [revision, setRevision] = useState(0);

  // "Other" is not an exchange code, so the admin has to name the real one; exchangeLabel stays visible above.
  const initialExchange =
    request.exchange === OTHER_EXCHANGE ? "" : request.exchange;
  const notes = request.notes.map(({ note, requestedAt }) => ({
    key: `${requestedAt}-${note}`,
    note,
    date: moment(requestedAt).format("D MMM YYYY"),
  }));

  const handleEditorDraftChange = useCallback(
    (editor: InstrumentEditorDraft | null) =>
      updateDraft((current) => toDraft(editor, current?.reason ?? "")),
    [updateDraft],
  );
  const handleReasonChange = useCallback(
    (reason: string) =>
      updateDraft((current) => toDraft(current?.editor ?? null, reason)),
    [updateDraft],
  );
  const handleDiscard = () => {
    updateDraft(() => null);
    setIsRejecting(false);
    setRevision((current) => current + 1);
    onClearError();
  };
  const openRejectForm = () => setIsRejecting(true);
  const closeRejectForm = () => setIsRejecting(false);

  return (
    <Card aria-label={`Review ${request.symbol}`}>
      <CardHeader>
        <CardTitle>{request.symbol}</CardTitle>
      </CardHeader>
      <DefinitionList>
        <dt>Requested exchange</dt>
        <dd>{request.exchangeLabel}</dd>
        <dt>Requesters</dt>
        <dd>{request.requesterCount}</dd>
      </DefinitionList>
      {notes.length > 0 && (
        <NoteList aria-label="Request notes">
          {notes.map(({ key, note, date }) => (
            <li key={key}>
              <NoteDate>{date}</NoteDate>
              {note}
            </li>
          ))}
        </NoteList>
      )}
      {draft && (
        <DraftNotice>
          Unsaved draft · kept while browsing
          <Button
            type={ButtonTypes.link}
            disabled={isSubmitting}
            onClick={handleDiscard}
          >
            Discard draft
          </Button>
        </DraftNotice>
      )}
      <div hidden={isRejecting}>
        <InstrumentEditor
          key={revision}
          initialIdentity={{
            symbol: request.symbol,
            exchange: initialExchange,
          }}
          submitLabel="Approve request"
          isSubmitting={isSubmitting}
          error={isRejecting ? null : error}
          draft={draft?.editor}
          onDraftChange={handleEditorDraftChange}
          onSubmit={onApprove}
        />
      </div>
      {isRejecting ? (
        <RejectRequestForm
          initialReason={draft?.reason ?? ""}
          isSubmitting={isSubmitting}
          error={error}
          onReasonChange={handleReasonChange}
          onReject={onReject}
          onCancel={closeRejectForm}
        />
      ) : (
        <div>
          <Button
            type={ButtonTypes.dangerOutline}
            disabled={isSubmitting}
            onClick={openRejectForm}
          >
            Reject request
          </Button>
        </div>
      )}
    </Card>
  );
};
